import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Activity,
  ArrowLeft,
  BarChart3,
  Crown,
  FileText,
  FlaskConical,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Shield,
  Sparkles,
  Trash2,
  Users
} from 'lucide-react';
import MainMenu from '@/components/MainMenu';
import AppLogo from '@/components/AppLogo';
import { publishedResourcePages as staticResourcePages } from '@/data/resourcePages';

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'pro', label: 'Pro' },
  { value: 'premium', label: 'Premium' }
];

function emptyBetaForm() {
  return {
    userId: '',
    userName: '',
    userEmail: '',
    password: '',
    enabled: true,
    assignedPlan: 'free',
    expiresAt: '',
    customLimits: {
      analyses: '',
      comparisons: '',
      resume_generations: ''
    },
    note: ''
  };
}

function formatPlanLabel(plan = 'free') {
  return `${plan.charAt(0).toUpperCase()}${plan.slice(1)}`;
}

function formatDate(value) {
  if (!value) return 'No expiration';
  return new Date(value).toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return 'No views yet';
  return new Date(value).toLocaleString();
}

function formatLimitDisplay(value) {
  if (value === null || value === undefined || value === '') return 'Plan default';
  if (value === -1) return 'Unlimited';
  return `${value}/month`;
}

function getStatusStyles(status) {
  const styles = {
    active: 'bg-emerald-100 text-emerald-700',
    expired: 'bg-amber-100 text-amber-700',
    disabled: 'bg-slate-100 text-slate-600'
  };

  return styles[status] || styles.disabled;
}

function formatResourceLabel(resourcePage) {
  if (!resourcePage?.slug) return resourcePage?.path || 'Unknown resource page';
  return resourcePage.slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function toBetaForm(account) {
  const betaAccess = account?.beta_access;
  return {
    userId: account?.user_id || '',
    userName: account?.name || '',
    userEmail: account?.email || '',
    password: '',
    enabled: betaAccess?.enabled ?? true,
    assignedPlan: betaAccess?.assigned_plan || account?.effective_plan || account?.plan || 'free',
    expiresAt: betaAccess?.expires_at ? betaAccess.expires_at.slice(0, 10) : '',
    customLimits: {
      analyses: betaAccess?.custom_limits?.analyses ?? '',
      comparisons: betaAccess?.custom_limits?.comparisons ?? '',
      resume_generations: betaAccess?.custom_limits?.resume_generations ?? ''
    },
    note: betaAccess?.note || ''
  };
}

function StatCard({ stat, index }) {
  return (
    <motion.div
      key={stat.title}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="rounded-xl border border-border bg-white p-6 shadow-soft"
      data-testid={`stat-${index}`}
    >
      <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-lg ${stat.color}`}>
        {stat.icon}
      </div>
      <p className="text-sm text-muted-foreground">{stat.title}</p>
      <p className="mt-1 font-heading text-3xl font-bold text-foreground">{stat.value.toLocaleString()}</p>
      <p className="mt-2 text-sm text-muted-foreground">{stat.detail}</p>
    </motion.div>
  );
}

function FoundationCard({ section, index }) {
  return (
    <motion.div
      key={section.title}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 + index * 0.08 }}
      className="rounded-xl border border-border bg-white p-6 shadow-soft"
      data-testid={`foundation-card-${index}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${section.accent}`}>
          {section.icon}
        </div>
        <span className="rounded-full bg-surface px-3 py-1 text-xs font-medium text-muted-foreground">
          {section.status}
        </span>
      </div>
      <h3 className="mt-4 font-heading text-xl font-semibold text-foreground">{section.title}</h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{section.description}</p>
      <ul className="mt-5 space-y-2">
        {section.items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm text-foreground">
            <span className="mt-1 h-2 w-2 rounded-full bg-primary/40" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [betaAccounts, setBetaAccounts] = useState([]);
  const [betaLoading, setBetaLoading] = useState(true);
  const [savingBeta, setSavingBeta] = useState(false);
  const [removingBeta, setRemovingBeta] = useState(false);
  const [betaFilter, setBetaFilter] = useState('');
  const [editorMode, setEditorMode] = useState('create');
  const [creationMode, setCreationMode] = useState('existing');
  const [selectedBetaId, setSelectedBetaId] = useState(null);
  const [betaForm, setBetaForm] = useState(emptyBetaForm());
  const [userSearch, setUserSearch] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await api.getAdminStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
      toast.error(error.response?.data?.detail || 'Failed to load admin stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadBetaAccounts = useCallback(async () => {
    setBetaLoading(true);
    try {
      const data = await api.getAdminBetaAccounts();
      setBetaAccounts(data);
    } catch (error) {
      console.error('Failed to load beta accounts:', error);
      toast.error(error.response?.data?.detail || 'Failed to load beta accounts');
    } finally {
      setBetaLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const data = await api.getAdminAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load admin analytics:', error);
      toast.error(error.response?.data?.detail || 'Failed to load admin analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadStats(), loadBetaAccounts(), loadAnalytics()]);
  }, [loadStats, loadBetaAccounts, loadAnalytics]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (editorMode !== 'create' || creationMode !== 'existing') {
      setUserSearchResults([]);
      setSearchingUsers(false);
      return;
    }

    const trimmedQuery = userSearch.trim();
    if (trimmedQuery.length < 2) {
      setUserSearchResults([]);
      setSearchingUsers(false);
      return;
    }

    let cancelled = false;
    setSearchingUsers(true);

    const timeoutId = setTimeout(async () => {
      try {
        const data = await api.searchAdminUsers(trimmedQuery, 12);
        if (!cancelled) {
          setUserSearchResults(data);
        }
      } catch (error) {
        console.error('Failed to search users:', error);
        if (!cancelled) {
          toast.error(error.response?.data?.detail || 'Failed to search users');
        }
      } finally {
        if (!cancelled) {
          setSearchingUsers(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [creationMode, editorMode, userSearch]);

  const betaSummary = useMemo(() => {
    return betaAccounts.reduce((summary, account) => {
      const status = account.beta_access?.status || 'disabled';
      summary.total += 1;
      summary[status] += 1;
      return summary;
    }, { total: 0, active: 0, expired: 0, disabled: 0 });
  }, [betaAccounts]);

  const filteredBetaAccounts = useMemo(() => {
    const query = betaFilter.trim().toLowerCase();
    if (!query) return betaAccounts;

    return betaAccounts.filter((account) => {
      const haystack = [
        account.name,
        account.email,
        account.beta_access?.assigned_plan,
        account.beta_access?.status,
        account.beta_access?.note
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [betaAccounts, betaFilter]);

  const totalUsers = stats?.total_users || 0;
  const totalAnalyses = stats?.total_analyses || 0;
  const proSubscriptions = stats?.pro_subscriptions || 0;
  const premiumSubscriptions = stats?.premium_subscriptions || 0;
  const paidAccounts = proSubscriptions + premiumSubscriptions;
  const freeAccounts = Math.max(totalUsers - paidAccounts, 0);
  const activeBetaAccounts = stats?.active_beta_accounts || 0;
  const configuredBetaAccounts = stats?.configured_beta_accounts || 0;
  const newUsersThisWeek = stats?.new_users_this_week || 0;
  const paidShare = totalUsers ? Math.round((paidAccounts / totalUsers) * 100) : 0;
  const staticResourcePageCount = staticResourcePages.length;
  const totalPageViews = analytics?.page_views?.total || 0;
  const landingPageViews = analytics?.page_views?.landing || 0;
  const pricingPageViews = analytics?.page_views?.pricing || 0;
  const resourcePageViews = analytics?.page_views?.resource || 0;
  const totalSignups = analytics?.signups?.total || 0;
  const freeSignups = analytics?.signups?.free || 0;
  const proSignups = analytics?.signups?.pro || 0;
  const premiumSignups = analytics?.signups?.premium || 0;
  const viewsLast7Days = analytics?.trend?.last_7_days || 0;
  const viewsPrevious7Days = analytics?.trend?.previous_7_days || 0;
  const viewTrendDelta = viewsLast7Days - viewsPrevious7Days;
  const topResourcePages = analytics?.top_resource_pages || [];

  const statCards = [
    {
      title: 'Total users',
      value: totalUsers,
      detail: `${freeAccounts.toLocaleString()} free accounts`,
      icon: <Users className="w-6 h-6" />,
      color: 'bg-primary/10 text-primary'
    },
    {
      title: 'Paid accounts',
      value: paidAccounts,
      detail: `${paidShare}% of current users`,
      icon: <Crown className="w-6 h-6" />,
      color: 'bg-amber-100 text-amber-700'
    },
    {
      title: 'Beta accounts',
      value: configuredBetaAccounts,
      detail: `${activeBetaAccounts.toLocaleString()} active right now`,
      icon: <FlaskConical className="w-6 h-6" />,
      color: 'bg-emerald-100 text-emerald-700'
    },
    {
      title: 'Resume activity',
      value: totalAnalyses,
      detail: 'Total analyses recorded',
      icon: <BarChart3 className="w-6 h-6" />,
      color: 'bg-sky-100 text-sky-700'
    },
    {
      title: 'Static SEO pages',
      value: staticResourcePageCount,
      detail: 'Managed in frontend for stable indexing',
      icon: <FileText className="w-6 h-6" />,
      color: 'bg-slate-100 text-slate-700'
    },
    {
      title: 'New users (7 days)',
      value: newUsersThisWeek,
      detail: `${proSubscriptions.toLocaleString()} Pro / ${premiumSubscriptions.toLocaleString()} Premium`,
      icon: <Sparkles className="w-6 h-6" />,
      color: 'bg-violet-100 text-violet-700'
    }
  ];

  const analyticsCards = [
    {
      title: 'Total page views',
      value: totalPageViews,
      detail: `${viewsLast7Days.toLocaleString()} in the last 7 days`
    },
    {
      title: 'Landing page views',
      value: landingPageViews,
      detail: 'Traffic to the homepage'
    },
    {
      title: 'Pricing page views',
      value: pricingPageViews,
      detail: 'Visits to the pricing page'
    },
    {
      title: 'Resource page views',
      value: resourcePageViews,
      detail: 'Across static resource pages and legacy guides'
    },
    {
      title: 'Total signups',
      value: totalSignups,
      detail: 'Self-serve account creations'
    },
    {
      title: 'Free signups',
      value: freeSignups,
      detail: 'Accounts created on the free entry plan'
    },
    {
      title: 'Pro signups',
      value: proSignups,
      detail: 'Users currently on Pro'
    },
    {
      title: 'Premium signups',
      value: premiumSignups,
      detail: 'Users currently on Premium'
    }
  ];

  const foundationCards = useMemo(() => ([
    {
      title: 'Account Management',
      description: 'A future operator workspace for reviewing users, investigating support issues, and handling account-level decisions without touching core auth flows.',
      icon: <Settings className="w-5 h-5" />,
      accent: 'bg-primary/10 text-primary',
      items: ['User lookup and profile review', 'Plan/access troubleshooting', 'Support-oriented admin actions'],
      status: 'Foundation ready'
    },
    {
      title: 'Beta Accounts',
      description: 'Backend-managed beta access is now live here, with assigned access levels, expiration, custom limits, and admin notes managed from a single place.',
      icon: <FlaskConical className="w-5 h-5" />,
      accent: 'bg-emerald-100 text-emerald-700',
      items: ['Enable or disable beta access', 'Assign Free, Pro, or Premium beta access', 'Override monthly limits and admin notes'],
      status: 'Live now'
    },
    {
      title: 'Resource Pages',
      description: 'SEO/resource pages are now managed as static frontend pages for more stable Google indexing.',
      icon: <FileText className="w-5 h-5" />,
      accent: 'bg-sky-100 text-sky-700',
      items: ['Content now lives in frontend data', 'Public URLs stay unchanged at `/resources/:slug`', 'Canonical and robots tags stay stable in the frontend'],
      status: 'Static now'
    },
    {
      title: 'Analytics',
      description: 'Basic backend-managed analytics now capture page views and signup totals, giving the admin dashboard a simple read on traffic and conversion activity.',
      icon: <Activity className="w-5 h-5" />,
      accent: 'bg-amber-100 text-amber-700',
      items: ['Landing, pricing, and resource page view counts', 'Signup totals from the existing user and plan model', 'Top-viewed resources and recent trend summary'],
      status: 'Live now'
    }
  ]), []);

  const handleRefresh = async () => {
    await refreshAll();
  };

  const handleStartCreate = (mode = 'existing') => {
    setEditorMode('create');
    setCreationMode(mode);
    setSelectedBetaId(null);
    setBetaForm(emptyBetaForm());
    setUserSearch('');
    setUserSearchResults([]);
  };

  const handleSelectAccount = (account) => {
    setEditorMode('edit');
    setCreationMode('existing');
    setSelectedBetaId(account.user_id);
    setBetaForm(toBetaForm(account));
    setUserSearch('');
    setUserSearchResults([]);
  };

  const handleSelectSearchUser = (account) => {
    setCreationMode('existing');
    setSelectedBetaId(account.user_id);
    setBetaForm(toBetaForm(account));
    setUserSearch('');
    setUserSearchResults([]);
    setEditorMode(account.beta_access ? 'edit' : 'create');
  };

  const handleFieldChange = (field, value) => {
    setBetaForm((current) => ({ ...current, [field]: value }));
  };

  const handleLimitChange = (field, value) => {
    setBetaForm((current) => ({
      ...current,
      customLimits: {
        ...current.customLimits,
        [field]: value
      }
    }));
  };

  const buildBetaSettings = () => {
    const parseLimit = (value) => {
      if (value === '' || value === null || value === undefined) return null;
      const parsed = Number(value);
      if (Number.isNaN(parsed)) {
        throw new Error('Custom limits must be numbers');
      }
      return parsed;
    };

    try {
      return {
        enabled: betaForm.enabled,
        assigned_plan: betaForm.assignedPlan,
        expires_at: betaForm.expiresAt || null,
        custom_limits: {
          analyses: parseLimit(betaForm.customLimits.analyses),
          comparisons: parseLimit(betaForm.customLimits.comparisons),
          resume_generations: parseLimit(betaForm.customLimits.resume_generations)
        },
        note: betaForm.note.trim() || null
      };
    } catch (error) {
      toast.error(error.message || 'Invalid beta limits');
      return null;
    }
  };

  const buildExistingBetaPayload = () => {
    if (!betaForm.userId) {
      toast.error('Select a user before saving beta access');
      return null;
    }

    const settings = buildBetaSettings();
    if (!settings) return null;

    return {
      user_id: betaForm.userId,
      ...settings
    };
  };

  const buildNewBetaUserPayload = () => {
    const trimmedName = betaForm.userName.trim();
    const trimmedEmail = betaForm.userEmail.trim();

    if (!trimmedName) {
      toast.error('Enter a name for the beta user');
      return null;
    }
    if (!trimmedEmail) {
      toast.error('Enter an email/login identifier');
      return null;
    }
    if (betaForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return null;
    }

    const settings = buildBetaSettings();
    if (!settings) return null;

    return {
      name: trimmedName,
      email: trimmedEmail,
      password: betaForm.password,
      ...settings
    };
  };

  const handleSaveBetaAccess = async () => {
    const payload =
      editorMode === 'edit' || creationMode === 'existing'
        ? buildExistingBetaPayload()
        : buildNewBetaUserPayload();
    if (!payload) return;

    setSavingBeta(true);
    try {
      const response = editorMode === 'edit'
        ? await api.updateAdminBetaAccount(payload.user_id, payload)
        : creationMode === 'new'
        ? await api.createAdminBetaUser(payload)
        : await api.createAdminBetaAccount(payload);

      toast.success(
        editorMode === 'edit'
          ? 'Beta access updated'
          : creationMode === 'new'
          ? 'New beta user created'
          : 'Beta access created'
      );
      setEditorMode('edit');
      setCreationMode('existing');
      setSelectedBetaId(response.user_id);
      setBetaForm(toBetaForm(response));
      setUserSearch('');
      await refreshAll();
    } catch (error) {
      console.error('Failed to save beta access:', error);
      toast.error(error.response?.data?.detail || 'Failed to save beta access');
    } finally {
      setSavingBeta(false);
    }
  };

  const handleRemoveBetaAccess = async () => {
    if (!betaForm.userId) return;

    setRemovingBeta(true);
    try {
      await api.removeAdminBetaAccount(betaForm.userId);
      toast.success('Beta access removed');
      handleStartCreate();
      await refreshAll();
    } catch (error) {
      console.error('Failed to remove beta access:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove beta access');
    } finally {
      setRemovingBeta(false);
    }
  };

  const isCreateMode = editorMode === 'create';
  const isExistingCreateMode = isCreateMode && creationMode === 'existing';
  const isNewUserCreateMode = isCreateMode && creationMode === 'new';
  const showBetaEditor = isNewUserCreateMode || Boolean(betaForm.userId);

  return (
    <div className="min-h-screen bg-surface" data-testid="admin-dashboard">
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <MainMenu />
            <button
              onClick={() => navigate('/dashboard')}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <AppLogo />
              <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-700">
                <Shield className="w-4 h-4" />
                Admin
              </span>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={statsLoading || betaLoading || analyticsLoading}
            className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface hover:text-foreground disabled:opacity-60"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${(statsLoading || betaLoading || analyticsLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <section className="mb-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              <Shield className="w-4 h-4" />
              Protected admin workspace
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              Admin Dashboard
            </h1>
            <p className="mt-3 text-base text-muted-foreground">
              Welcome back, {user?.name}. This admin workspace manages beta access and lightweight analytics, while SEO/resource pages are now managed as static frontend pages for more stable Google indexing.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:min-w-[36rem] lg:grid-cols-4">
            <div className="rounded-xl border border-border bg-white px-4 py-3 shadow-soft">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Access</p>
              <p className="mt-1 text-sm font-semibold text-foreground">Admins only</p>
            </div>
            <div className="rounded-xl border border-border bg-white px-4 py-3 shadow-soft">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Beta system</p>
              <p className="mt-1 text-sm font-semibold text-foreground">Backend managed</p>
            </div>
            <div className="rounded-xl border border-border bg-white px-4 py-3 shadow-soft">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">SEO pages</p>
              <p className="mt-1 text-sm font-semibold text-foreground">Static frontend</p>
            </div>
            <div className="rounded-xl border border-border bg-white px-4 py-3 shadow-soft">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Analytics</p>
              <p className="mt-1 text-sm font-semibold text-foreground">Page views tracked</p>
            </div>
          </div>
        </section>

        <section className="mb-10">
          <div className="mb-4">
            <h2 className="font-heading text-xl font-semibold text-foreground">Live Snapshot</h2>
            <p className="text-sm text-muted-foreground">Current high-level numbers from the admin workspace, including beta, static SEO page, and activity totals.</p>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-6">
            {statCards.map((stat, index) => (
              <StatCard key={stat.title} stat={stat} index={index} />
            ))}
          </div>
        </section>

        <section className="mb-10">
          <div className="mb-4">
            <h2 className="font-heading text-xl font-semibold text-foreground">Foundation Sections</h2>
            <p className="text-sm text-muted-foreground">
              The broader admin roadmap stays cleanly separated while Beta Accounts becomes a working tool in this pass.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {foundationCards.map((section, index) => (
              <FoundationCard key={section.title} section={section} index={index} />
            ))}
          </div>
        </section>

        <section className="mb-10">
          <div className="mb-4">
            <h2 className="font-heading text-xl font-semibold text-foreground">Analytics</h2>
            <p className="text-sm text-muted-foreground">
              Lightweight backend-managed tracking for key page views and signup totals, surfaced here without adding a separate analytics vendor.
            </p>
          </div>

          {analyticsLoading ? (
            <div className="rounded-xl border border-border bg-white px-6 py-12 shadow-soft">
              <div className="flex items-center justify-center text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading analytics summary...
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
                {analyticsCards.map((card) => (
                  <div key={card.title} className="rounded-xl border border-border bg-white p-5 shadow-soft">
                    <p className="text-sm text-muted-foreground">{card.title}</p>
                    <p className="mt-2 font-heading text-3xl font-bold text-foreground">{card.value.toLocaleString()}</p>
                    <p className="mt-2 text-sm text-muted-foreground">{card.detail}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[0.8fr_1.2fr]">
                <div className="rounded-xl border border-border bg-white p-6 shadow-soft">
                  <div className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-amber-600" />
                    <h3 className="font-heading text-lg font-semibold text-foreground">Recent View Trend</h3>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    A simple comparison of the last 7 days of page-view traffic against the 7 days before that.
                  </p>

                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between rounded-lg bg-surface p-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">Last 7 days</p>
                        <p className="text-sm text-muted-foreground">Recent tracked page views</p>
                      </div>
                      <p className="font-heading text-2xl font-bold text-foreground">{viewsLast7Days.toLocaleString()}</p>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-surface p-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">Previous 7 days</p>
                        <p className="text-sm text-muted-foreground">The prior comparison window</p>
                      </div>
                      <p className="font-heading text-2xl font-bold text-foreground">{viewsPrevious7Days.toLocaleString()}</p>
                    </div>
                    <div className="rounded-lg border border-border px-4 py-4">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Trend summary</p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        {viewTrendDelta === 0
                          ? 'Traffic is flat week over week'
                          : `${viewTrendDelta > 0 ? '+' : ''}${viewTrendDelta.toLocaleString()} page views week over week`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-white p-6 shadow-soft">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-sky-600" />
                    <h3 className="font-heading text-lg font-semibold text-foreground">Top Viewed Resource Pages</h3>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    The most-viewed resource content across the static frontend resource pages and the legacy hardcoded SEO guides.
                  </p>

                  <div className="mt-6 space-y-3">
                    {topResourcePages.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-border bg-surface px-6 py-10 text-center">
                        <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
                        <h4 className="font-medium text-foreground">No resource page views yet</h4>
                        <p className="mt-2 text-sm text-muted-foreground">
                          Once people start visiting resource pages, the top performers will show up here.
                        </p>
                      </div>
                    ) : (
                      topResourcePages.map((page) => (
                        <div key={page.path} className="flex items-start justify-between gap-4 rounded-lg bg-surface p-4">
                          <div>
                            <p className="font-medium text-foreground">{formatResourceLabel(page)}</p>
                            <p className="mt-1 text-sm text-muted-foreground">{page.path}</p>
                            <p className="mt-2 text-xs text-muted-foreground">Last viewed {formatDateTime(page.last_viewed_at)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-heading text-2xl font-bold text-foreground">{page.views.toLocaleString()}</p>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">views</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </section>

        <section className="mb-10">
          <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="font-heading text-xl font-semibold text-foreground">Beta Accounts</h2>
              <p className="text-sm text-muted-foreground">
                Manage beta access for existing users and create brand-new beta users with placeholder login emails, hashed passwords, assigned access, and custom limits.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full bg-white px-3 py-1 text-sm font-medium text-foreground shadow-soft border border-border">
                {betaSummary.total} configured
              </span>
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700">
                {betaSummary.active} active
              </span>
              <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-700">
                {betaSummary.expired} expired
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600">
                {betaSummary.disabled} disabled
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-xl border border-border bg-white p-6 shadow-soft" data-testid="beta-accounts-list">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">Configured Beta Users</h3>
                  <p className="text-sm text-muted-foreground">Search, review, and open beta-enabled accounts for editing.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleStartCreate('existing')}
                    className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                      isExistingCreateMode
                        ? 'bg-primary text-white hover:bg-primary-hover'
                        : 'border border-border bg-white text-foreground hover:bg-surface'
                    }`}
                    data-testid="new-beta-account-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Existing User Access
                  </button>
                  <button
                    onClick={() => handleStartCreate('new')}
                    className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                      isNewUserCreateMode
                        ? 'bg-primary text-white hover:bg-primary-hover'
                        : 'border border-border bg-white text-foreground hover:bg-surface'
                    }`}
                    data-testid="new-beta-user-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Create New Beta User
                  </button>
                </div>
              </div>

              <div className="relative mt-5">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={betaFilter}
                  onChange={(e) => setBetaFilter(e.target.value)}
                  placeholder="Filter beta accounts by name, email, note, or plan"
                  className="h-11 w-full rounded-lg border border-border bg-white pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  data-testid="beta-filter-input"
                />
              </div>

              <div className="mt-5 space-y-3">
                {betaLoading ? (
                  <div className="flex items-center justify-center py-12 text-muted-foreground">
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Loading beta accounts...
                  </div>
                ) : filteredBetaAccounts.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border bg-surface px-6 py-10 text-center">
                    <FlaskConical className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
                    <h4 className="font-medium text-foreground">No beta accounts yet</h4>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Start a beta program by selecting an existing user or creating a brand-new beta user on the right.
                    </p>
                  </div>
                ) : (
                  filteredBetaAccounts.map((account) => {
                    const isSelected = selectedBetaId === account.user_id && editorMode === 'edit';
                    const betaAccess = account.beta_access;
                    const status = betaAccess?.status || 'disabled';

                    return (
                      <button
                        key={account.user_id}
                        type="button"
                        onClick={() => handleSelectAccount(account)}
                        className={`w-full rounded-xl border p-4 text-left transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/5 shadow-soft'
                            : 'border-border hover:border-primary/30 hover:bg-surface'
                        }`}
                        data-testid={`beta-account-${account.user_id}`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <p className="font-medium text-foreground">{account.name}</p>
                            <p className="text-sm text-muted-foreground">{account.email}</p>
                          </div>
                          <span className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-medium ${getStatusStyles(status)}`}>
                            {status}
                          </span>
                        </div>
                        <div className="mt-4 grid gap-3 text-sm text-muted-foreground sm:grid-cols-3">
                          <div>
                            <p className="text-xs uppercase tracking-wide">Assigned access</p>
                            <p className="mt-1 font-medium text-foreground">
                              {formatPlanLabel(betaAccess?.assigned_plan || account.effective_plan || account.plan)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide">Expiration</p>
                            <p className="mt-1 font-medium text-foreground">
                              {formatDate(betaAccess?.expires_at)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide">Limit overrides</p>
                            <p className="mt-1 font-medium text-foreground">
                              {[
                                betaAccess?.custom_limits?.analyses,
                                betaAccess?.custom_limits?.comparisons,
                                betaAccess?.custom_limits?.resume_generations
                              ].some((value) => value !== null && value !== undefined)
                                ? 'Custom'
                                : 'Plan default'}
                            </p>
                          </div>
                        </div>
                        {betaAccess?.note && (
                          <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{betaAccess.note}</p>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-white p-6 shadow-soft" data-testid="beta-account-editor">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    {editorMode === 'edit'
                      ? 'Edit Beta Access'
                      : creationMode === 'new'
                      ? 'Create New Beta User'
                      : 'Create Beta Access'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {editorMode === 'edit'
                      ? 'Update the selected beta account and save changes back to the backend.'
                      : creationMode === 'new'
                      ? 'Create a brand-new beta user with a placeholder login email, password, and beta access settings.'
                      : 'Select an existing user, then assign beta access, expiration, and any limit overrides.'}
                  </p>
                </div>
                {editorMode === 'edit' && (
                  <button
                    onClick={() => handleStartCreate('existing')}
                    className="rounded-lg border border-border px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface hover:text-foreground"
                  >
                    New Setup
                  </button>
                )}
              </div>

              {isCreateMode && (
                <div className="mt-6 inline-flex rounded-xl border border-border bg-surface p-1">
                  <button
                    type="button"
                    onClick={() => handleStartCreate('existing')}
                    className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                      creationMode === 'existing'
                        ? 'bg-white text-foreground shadow-soft'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Existing User
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStartCreate('new')}
                    className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                      creationMode === 'new'
                        ? 'bg-white text-foreground shadow-soft'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Create New Beta User
                  </button>
                </div>
              )}

              {isExistingCreateMode && (
                <div className="mt-6">
                  <label className="mb-2 block text-sm font-medium text-foreground">Find Existing User</label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search by name or email"
                      className="h-11 w-full rounded-lg border border-border bg-white pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      data-testid="beta-user-search-input"
                    />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Start typing at least two characters to find an existing account.
                  </p>

                  {searchingUsers && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Searching users...
                    </div>
                  )}

                  {!searchingUsers && userSearch.trim().length >= 2 && userSearchResults.length > 0 && (
                    <div className="mt-4 max-h-56 space-y-2 overflow-y-auto rounded-xl border border-border bg-surface p-3">
                      {userSearchResults.map((account) => (
                        <button
                          key={account.user_id}
                          type="button"
                          onClick={() => handleSelectSearchUser(account)}
                          className="w-full rounded-lg bg-white px-4 py-3 text-left transition-colors hover:bg-primary/5"
                          data-testid={`user-search-result-${account.user_id}`}
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <p className="font-medium text-foreground">{account.name}</p>
                              <p className="text-sm text-muted-foreground">{account.email}</p>
                            </div>
                            {account.beta_access && (
                              <span className={`rounded-full px-3 py-1 text-xs font-medium ${getStatusStyles(account.beta_access.status)}`}>
                                {account.beta_access.status}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {showBetaEditor ? (
                <div className="mt-6 space-y-5">
                  {isNewUserCreateMode ? (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label className="mb-2 block text-sm font-medium text-foreground">Name</label>
                        <input
                          type="text"
                          value={betaForm.userName}
                          onChange={(e) => handleFieldChange('userName', e.target.value)}
                          placeholder="Beta user name"
                          className="h-11 w-full rounded-lg border border-border bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                          data-testid="new-beta-user-name"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-foreground">Email / Login Identifier</label>
                        <input
                          type="email"
                          value={betaForm.userEmail}
                          onChange={(e) => handleFieldChange('userEmail', e.target.value)}
                          placeholder="alex@betajobfit.com"
                          className="h-11 w-full rounded-lg border border-border bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                          data-testid="new-beta-user-email"
                        />
                        <p className="mt-2 text-xs text-muted-foreground">
                          Placeholder emails like `name@betajobfit.com` are valid here and do not need a working inbox.
                        </p>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-foreground">Password</label>
                        <input
                          type="password"
                          value={betaForm.password}
                          onChange={(e) => handleFieldChange('password', e.target.value)}
                          placeholder="At least 8 characters"
                          className="h-11 w-full rounded-lg border border-border bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                          data-testid="new-beta-user-password"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-border bg-surface px-4 py-4">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Selected user
                      </p>
                      <p className="mt-2 font-medium text-foreground">{betaForm.userName}</p>
                      <p className="text-sm text-muted-foreground">{betaForm.userEmail}</p>
                    </div>
                  )}

                  <label className="flex items-center gap-3 rounded-xl border border-border px-4 py-3">
                    <input
                      type="checkbox"
                      checked={betaForm.enabled}
                      onChange={(e) => handleFieldChange('enabled', e.target.checked)}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground">Enable beta access</p>
                      <p className="text-xs text-muted-foreground">Disabled or expired beta accounts automatically fall back to their normal plan.</p>
                    </div>
                  </label>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-foreground">Assigned Access Level</label>
                      <select
                        value={betaForm.assignedPlan}
                        onChange={(e) => handleFieldChange('assignedPlan', e.target.value)}
                        className="h-11 w-full rounded-lg border border-border bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        data-testid="beta-assigned-plan"
                      >
                        {PLAN_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-foreground">Expiration Date</label>
                      <input
                        type="date"
                        value={betaForm.expiresAt}
                        onChange={(e) => handleFieldChange('expiresAt', e.target.value)}
                        className="h-11 w-full rounded-lg border border-border bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        data-testid="beta-expiration-date"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <label className="text-sm font-medium text-foreground">Custom Monthly Limits</label>
                      <span className="text-xs text-muted-foreground">Leave blank for plan defaults. Use -1 for unlimited.</span>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div>
                        <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Analyses</label>
                        <input
                          type="number"
                          min="-1"
                          step="1"
                          value={betaForm.customLimits.analyses}
                          onChange={(e) => handleLimitChange('analyses', e.target.value)}
                          className="h-11 w-full rounded-lg border border-border bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                          data-testid="beta-limit-analyses"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Comparisons</label>
                        <input
                          type="number"
                          min="-1"
                          step="1"
                          value={betaForm.customLimits.comparisons}
                          onChange={(e) => handleLimitChange('comparisons', e.target.value)}
                          className="h-11 w-full rounded-lg border border-border bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                          data-testid="beta-limit-comparisons"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Resume generations</label>
                        <input
                          type="number"
                          min="-1"
                          step="1"
                          value={betaForm.customLimits.resume_generations}
                          onChange={(e) => handleLimitChange('resume_generations', e.target.value)}
                          className="h-11 w-full rounded-lg border border-border bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                          data-testid="beta-limit-generations"
                        />
                      </div>
                    </div>
                    <div className="mt-3 rounded-xl bg-surface px-4 py-3 text-xs text-muted-foreground">
                      Current override summary: analyses {formatLimitDisplay(betaForm.customLimits.analyses)}, comparisons {formatLimitDisplay(betaForm.customLimits.comparisons)}, resume generations {formatLimitDisplay(betaForm.customLimits.resume_generations)}.
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-foreground">Admin Note</label>
                    <textarea
                      value={betaForm.note}
                      onChange={(e) => handleFieldChange('note', e.target.value)}
                      rows={4}
                      placeholder="Optional admin context for why this beta access exists."
                      className="w-full rounded-lg border border-border bg-white px-3 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      data-testid="beta-note"
                    />
                  </div>

                  {editorMode === 'edit' && (
                    <div className="rounded-xl border border-border bg-surface px-4 py-4 text-sm">
                      <p className="font-medium text-foreground">Current beta status</p>
                      <div className="mt-2 space-y-1 text-muted-foreground">
                        <p>Status: <span className="font-medium text-foreground capitalize">{betaForm.enabled ? 'enabled' : 'disabled'}</span></p>
                        <p>Assigned access: <span className="font-medium text-foreground">{formatPlanLabel(betaForm.assignedPlan)}</span></p>
                        <p>Expires: <span className="font-medium text-foreground">{betaForm.expiresAt ? formatDate(betaForm.expiresAt) : 'No expiration'}</span></p>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      onClick={handleSaveBetaAccess}
                      disabled={savingBeta || removingBeta}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-60"
                      data-testid="save-beta-access-btn"
                    >
                      {savingBeta ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Shield className="h-4 w-4" />
                      )}
                      {editorMode === 'edit'
                        ? 'Save Beta Changes'
                        : creationMode === 'new'
                        ? 'Create Beta User'
                        : 'Create Beta Access'}
                    </button>
                    {editorMode === 'edit' && (
                      <button
                        onClick={handleRemoveBetaAccess}
                        disabled={savingBeta || removingBeta}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-60"
                        data-testid="remove-beta-access-btn"
                      >
                        {removingBeta ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        Remove Beta Access
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-6 rounded-xl border border-dashed border-border bg-surface px-6 py-10 text-center">
                  <Users className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
                  <h4 className="font-medium text-foreground">Pick a user to begin</h4>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Search for an existing user, create a new beta user, or choose a configured beta account from the list to edit it.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 lg:grid-cols-[1.35fr_0.65fr]">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="rounded-xl border border-border bg-white p-6 shadow-soft"
            data-testid="subscriber-mix-card"
          >
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              <h3 className="font-heading text-lg font-semibold text-foreground">Subscriber Mix</h3>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              A simple read on who is using paid access today, separate from beta overrides and without introducing new analytics work yet.
            </p>
            <div className="space-y-4">
              <div className="mt-5 flex items-center justify-between rounded-lg bg-surface p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <Crown className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Pro Plan</p>
                    <p className="text-sm text-muted-foreground">Current paid subscriptions</p>
                  </div>
                </div>
                <p className="font-heading text-xl font-bold text-foreground">{proSubscriptions.toLocaleString()}</p>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-surface p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                    <Crown className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium">Premium Plan</p>
                    <p className="text-sm text-muted-foreground">Current paid subscriptions</p>
                  </div>
                </div>
                <p className="font-heading text-xl font-bold text-foreground">{premiumSubscriptions.toLocaleString()}</p>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-4">
                <p className="font-medium text-foreground">Free accounts</p>
                <p className="font-heading text-2xl font-bold text-foreground">{freeAccounts.toLocaleString()}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="rounded-xl border border-border bg-white p-6 shadow-soft"
            data-testid="admin-foundation-status"
          >
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold text-foreground">Current Admin Capabilities</h3>
            </div>
            <div className="space-y-4">
              <div className="mt-5 rounded-lg bg-surface p-4">
                <p className="text-sm font-medium text-foreground">Backend beta enforcement</p>
                <p className="mt-1 text-sm text-muted-foreground">Assigned plan access, custom limits, expiration, and disable state now all resolve on the server.</p>
              </div>
              <div className="rounded-lg bg-surface p-4">
                <p className="text-sm font-medium text-foreground">Basic analytics tracking</p>
                <p className="mt-1 text-sm text-muted-foreground">Page views and signup totals now flow into the admin dashboard through lightweight backend-managed tracking.</p>
              </div>
              <div className="rounded-lg bg-surface p-4">
                <p className="text-sm font-medium text-foreground">Admin beta management</p>
                <p className="mt-1 text-sm text-muted-foreground">Admins can search users, create beta access, update settings, and remove beta access from this page.</p>
              </div>
              <div className="rounded-lg bg-surface p-4">
                <p className="text-sm font-medium text-foreground">Existing auth model preserved</p>
                <p className="mt-1 text-sm text-muted-foreground">No second login system was introduced, and `/admin` remains protected by the current admin route pattern.</p>
              </div>
            </div>
          </motion.div>
        </section>
      </main>
    </div>
  );
}
